import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  getStoreUrlForPlatform,
  openNativeStoreUrl,
  type ForceUpdateState,
} from "@/hooks/useNativeForceUpdate";
import { useLanguage } from "@/context/LanguageContext";
import { nativeAppLabels } from "@/i18n/labels/commonLabels";

type NativeForceUpdateModalProps = Pick<
  ForceUpdateState,
  "required" | "config" | "currentVersion"
>;

export function NativeForceUpdateModal({
  required,
  config,
  currentVersion,
}: NativeForceUpdateModalProps) {
  const { language } = useLanguage();
  const t = nativeAppLabels(language);

  useEffect(() => {
    if (!required) return;
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    html.classList.add("bilenia-native-force-update-active");
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      html.classList.remove("bilenia-native-force-update-active");
    };
  }, [required]);

  if (!required || !config) return null;

  const storeUrl = getStoreUrlForPlatform(config);

  return createPortal(
    <div
      className="native-force-update-overlay fixed inset-0 z-[9999] flex min-h-[100dvh] w-screen items-center justify-center bg-black/85 p-6 touch-none"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="native-force-update-title"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl text-center space-y-4 touch-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="native-force-update-title" className="text-xl font-semibold">
          {t.forceUpdateTitle}
        </h2>
        <p className="text-sm text-muted-foreground">{t.forceUpdateBody}</p>
        <p className="text-xs text-muted-foreground">
          {t.forceUpdateYourVersion}: {currentVersion} · {t.forceUpdateRequired}:{" "}
          {config.minAppVersion}
        </p>
        <Button
          type="button"
          className="w-full"
          size="lg"
          disabled={!storeUrl}
          onClick={() => void openNativeStoreUrl(storeUrl)}
        >
          {storeUrl ? t.forceUpdate : t.forceUpdateNoStore}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
